import { useMemo } from "react";

import {
	Card,
	Tab,
	TableBody,
	TableCell,
	TableColumn,
	TableHeader,
	TableRow,
	Tabs,
} from "@heroui/react";
import { Icon } from "@iconify/react";

import { useLanguage } from "@onesolution/hooks";
import { formatFrontDate } from "@onesolution/utils";

import type { TranslationsKeys } from "@onesolution/hooks/useLanguage";
import type { OperationDetails } from "@onesolution/serverFn";
import { AnalyticCard } from "../AnalyticCard";
import Chip from "../Chip";
import Messenger from "../Messenger/Messenger";
import ProgressBar from "../ProgressBar";
import Table, { baseTableBodyProps, baseTableRowProps } from "../Table/Table";

export type OperationDetailsTabs =
	| "overview"
	| "files"
	| "analytics"
	| "activity";

type OperationDetailsProps = {
	operation?: OperationDetails;
	defaultOpenedTab?: OperationDetailsTabs;
	onlyChat?: boolean;
};

export function DetailsWithChips({
	label,
	chips,
}: {
	label: string;
	chips: Array<{
		label?: string;
		link?: string;
		color?:
			| "default"
			| "primary"
			| "secondary"
			| "success"
			| "warning"
			| "danger"
			| undefined;
	}>;
}) {
	const filteredChip = useMemo(
		() => chips.filter(({ label }) => label),
		[chips],
	);

	if (!filteredChip.length) return null;

	return (
		<div className="flex gap-2">
			<span className="opacity-70">{label}</span>
			{filteredChip.map((chip) => (
				<Chip
					key={chip.label}
					color={chip.color}
					className={chip.link && "cursor-pointer"}
					radius="sm"
					endContent={
						chip.link ? (
							<Icon className="ml-2" icon="solar:square-share-line-linear" />
						) : null
					}
				>
					<p>{chip.label}</p>
				</Chip>
			))}
		</div>
	);
}

export default function OperationDetailsView({
	operation,
	onlyChat,
	defaultOpenedTab,
}: OperationDetailsProps) {
	if (!operation) return null;

	const { t } = useLanguage();
	const tagChips = useMemo(
		() =>
			[operation.marketingPlans, operation.campaigns, operation.services]
				.filter((i) => i)
				.flatMap((i) =>
					i.display_value
						?.split(",")
						.map((label) => ({ label, color: "default" })),
				)
				.concat(
					operation.entities?.map((entitie) => ({
						label: entitie.label,
						color: "primary",
					})) || [],
				) as { label: string; color: "primary" | "default" }[],
		[operation],
	);

	return (
		<Tabs
			aria-label="Options"
			classNames={{
				tabList: "w-full",
				base: "w-full",
			}}
			defaultSelectedKey={defaultOpenedTab}
		>
			{!onlyChat && (
				<Tab
					key="overview"
					title={t("basic.overview")}
					className="w-full flex flex-1"
				>
					<div className="w-full flex flex-col gap-4">
						<Card
							className="p-4 hover:shadow-md shadow-sm transition-all"
							shadow="none"
						>
							<h6 className="font-normal text-xl mb-4">
								{t("basic.progress")}
							</h6>
							<Table
								aria-label="progress"
								hasNextPage={false}
								isFetching={false}
								classNames={{ wrapper: "shadow-none! px-0" }}
							>
								<TableHeader>
									<TableColumn>{null}</TableColumn>
									<TableColumn width={100}>{t("basic.progress")}</TableColumn>
									<TableColumn width={150}>{t("basic.deadline")}</TableColumn>
									<TableColumn width={100}>{t("basic.proof")}</TableColumn>
								</TableHeader>
								<TableBody {...baseTableBodyProps(t)}>
									{operation.projects.map((project) => (
										<TableRow key={project.name} {...baseTableRowProps()}>
											<TableCell>
												<div className="flex justify-between items-center">
													<p>{project.name}</p>
													{project.ziflowStatus.label && (
														<Chip variant="bordered">
															<div className="flex gap-2 items-center">
																<span
																	className="h-2 w-2 rounded-full"
																	style={{
																		backgroundColor: project.ziflowStatus.color,
																	}}
																/>
																{t(
																	`status.${project.ziflowStatus.label}` as TranslationsKeys,
																	{
																		skeleton: false,
																	},
																)}
															</div>
														</Chip>
													)}
												</div>
											</TableCell>
											<TableCell>
												<ProgressBar status={project.statusGlobal} />
											</TableCell>
											<TableCell>{formatFrontDate(project.deadline)}</TableCell>
											<TableCell>
												{project.ziflow ? (
													<Chip
														link={project.ziflow}
														variant="flat"
														color="primary"
													>
														{t("operation.proof-link")}
													</Chip>
												) : null}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</Card>
						{operation.description && (
							<Card className="p-4" shadow="sm">
								<h6 className="font-normal mb-4">{t("basic.description")}</h6>
								<p>{operation.description}</p>
							</Card>
						)}
						<Card
							className="p-4 hover:shadow-md shadow-sm transition-all"
							shadow="none"
						>
							<h6 className="font-normal text-xl mb-4">{t("basic.details")}</h6>
							<div className="flex flex-col gap-4">
								<DetailsWithChips
									label={t("basic.creation-date")}
									chips={[{ label: formatFrontDate(operation.creationDate) }]}
								/>
								{operation.deadline && (
									<DetailsWithChips
										label={t("basic.deadline")}
										chips={[
											{
												label: formatFrontDate(operation.deadline),
											},
										]}
									/>
								)}
								{operation.timelineOriginal && (
									<DetailsWithChips
										label={t("basic.timeline")}
										chips={[
											{
												label: `${formatFrontDate(operation.timelineOriginal.from)} - ${formatFrontDate(operation.timelineOriginal.to)}`,
											},
										]}
									/>
								)}
								<DetailsWithChips label={t("name.tags")} chips={tagChips} />
							</div>
						</Card>
						{operation.subitems.length > 0 && (
							<Card
								className="p-4 hover:shadow-md shadow-sm transition-all"
								shadow="none"
							>
								<Table
									aria-label="product & services"
									hasNextPage={false}
									isFetching={false}
								>
									<TableHeader>
										<TableColumn>
											{t("name.product")} & {t("name.services")}
										</TableColumn>
										<TableColumn width={150}>
											{t("basic.quantities")}
										</TableColumn>
									</TableHeader>
									<TableBody>
										{operation.subitems.map(({ name, quantity }) => (
											<TableRow key={name}>
												<TableCell>{name}</TableCell>
												<TableCell>{quantity}</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</Card>
						)}
						{operation.files?.length > 0 && (
							<Card
								className="p-4 hover:shadow-md shadow-sm transition-all"
								shadow="none"
							>
								<h6 className="font-normal text-xl mb-4">{t("name.files")}</h6>

								<div>
									{operation.files.map((file) => (
										<div key={file.name} className="flex gap-4 items-center">
											<Icon
												icon="solar:file-download-line-duotone"
												className="text-3xl opacity-50 transition-all hover:opacity-100 cursor-pointer"
												onClick={() => window.open(file.url, "_blank")}
											/>
											<p>{file.name}</p>
										</div>
									))}
								</div>
							</Card>
						)}
					</div>
				</Tab>
			)}
			<Tab
				key="activity"
				title={t("basic.activity")}
				className="flex flex-1 w-full"
			>
				<Messenger messages={operation.messages} operationId={operation.id} />
			</Tab>
			{!onlyChat && (
				<Tab
					key="analytics"
					title={t("name.analytics")}
					className="flex flex-1"
				>
					<div className="grid grid-cols-2 h-fit gap-4">
						<AnalyticCard
							label={t("analytics.expenses")}
							value={`${operation.expenses || "0"} €`}
						/>
						<AnalyticCard
							label={t("analytics.number-version")}
							value={operation.versionCount?.toString() || "0"}
						/>
						<AnalyticCard
							label={t("analytics.support-duration")}
							value={`${operation.supportDuration || 0} ${t("basic.days", { capitalize: false })}`}
						/>
						<AnalyticCard
							label={t("analytics.completion-duration")}
							value={`${operation.completionDuration || 0} ${t("basic.days", { capitalize: false })}`}
						/>
					</div>
				</Tab>
			)}
		</Tabs>
	);
}
